import { EFirebaseContext, EOrganization, ERef } from '../types';
import { PaymentGateway } from '../types/invoices';
import { getInheritedData } from '../utils/inheritance';
import { SyncFormat } from '../types/integrations/sync';
import { getNewspaperErrorInfoForNewspaperIdWithDefaults } from '../errors/templateErrors';
import { ConfigurationError } from '../errors/ColumnErrors';
import { getErrorReporter } from '../utils/errors';
import { ColumnService } from '../services/directory';

export type PaymentGatewayAndAccountCode =
  | {
      gateway: PaymentGateway.PAYWAY;
      accountCode: SyncFormat;
    }
  | {
      gateway: Exclude<PaymentGateway, PaymentGateway.PAYWAY>;
      accountCode: undefined;
    };

/**
 * Determine the payment gateway and account code (if applicable) for the given organization.
 * If no organization is provided, defaults to Stripe with no account code.
 * For Payway, the account code corresponds to the sync format required for transactions,
 * which corresponds to the payway account used for processing.
 */
export const getOrganizationGatewayAndAccountCode = async (
  ctx: EFirebaseContext,
  organization: ERef<EOrganization> | undefined
): Promise<PaymentGatewayAndAccountCode> => {
  if (!organization) {
    return { gateway: PaymentGateway.STRIPE, accountCode: undefined };
  }
  const inheritanceProxy = await getInheritedData(organization);
  const { gateway } = inheritanceProxy;
  switch (gateway) {
    case PaymentGateway.PAYWAY:
      if (!inheritanceProxy.xmlExport?.format) {
        const newspaperInfo =
          await getNewspaperErrorInfoForNewspaperIdWithDefaults(
            ctx,
            organization.id
          );
        const err = new ConfigurationError({
          message: 'Newspaper is missing a Payway sync format',
          toFix:
            'Setup the Payway sync format for the newspaper by going to admin => Integrations => Export Settings, and enter the correct template info under "Format"',
          newspaperInfo
        });
        getErrorReporter().logAndCaptureError(
          ColumnService.CSMS,
          err,
          'Failed to get pay invoice data for public notice invoice due to missing Payway sync format'
        );
        throw err;
      }
      return {
        gateway,
        accountCode: inheritanceProxy.xmlExport.format
      };
    case PaymentGateway.ELAVON:
      return {
        gateway,
        accountCode: undefined
      };
    case PaymentGateway.STRIPE:
      return {
        gateway,
        accountCode: undefined
      };
    default:
      return {
        gateway: PaymentGateway.STRIPE,
        accountCode: undefined
      };
  }
};
